<template lang="pug">
.calendar-of-month
  .d-flex.flex-row.flex-wrap.mb-2(v-if="this.$slots.description || showChangeMonthBtn")
    .d-flex.flex-fill
      slot(name="description")
    .d-flex.flex-row.justify-content-end.align-items-start.flex-wrap(v-if="showChangeMonthBtn")
      button.btn.change-month-btn.mr-2(@click="onPreviousMonth") ＜ 前の月
      button.btn.change-month-btn.mr-2(@click="onCurrentMonth") 今月
      button.btn.change-month-btn(@click="onNextMonth") 次の月 ＞
  .week-header.d-flex.flex-row
    .date(v-for="(weekHeader, index) in weekHeaders" :class="getWeekHeaderClass(weekHeader, small)" :key="index") {{ weekHeader.name }}
  .week.d-flex.flex-row(v-for="(week, index) in calendar" :key="'week_' + index")
    .date(v-for="(date, index) in week" :key="date.date"
      :class="{ 'is-non-current-month': !date.isCurrentMonth, 'is-current-date': date.isCurrentDate }" :style="{ 'min-height' : cellHeight }")
      .date-num.mt-2.mb-2(:class="getDateClass(date)")
        span(v-if="date.showMonth") {{ date.month + 1 }}月
        span {{ date.date }}日
        span.ml-2 {{ getHolidayName(date) }}
      .events(v-for="event in date.events" :key="`${date.date}_${event.id}`")
        .event.mb-1(v-if="event.hitomeQEvent.width"
          :id="`${date.date}_${event.id}`"
          :style="createEventStyle(event)"
          @click="onEvent(event)"
        )
          //- event local event
          .facility-event(v-if="event.hitomeQEvent.type == 1")
            span.start-time.mr-2 {{ convertIso2Time(event.start.dateTime) }}
            img.local.mr-1(src="@/assets/calendar-event-local.png")
            span.summary {{ event.summary }}
          //- event online event
          .facility-event(v-else-if="event.hitomeQEvent.type == 2")
            span.start-time.mr-2 {{ convertIso2Time(event.start.dateTime) }}
            img.mr-1(src="@/assets/calendar-event-online.png")
            span.summary {{ event.summary }}
          //- event local and online event
          .facility-event(v-else-if="event.hitomeQEvent.type == 3")
            span.start-time.mr-2 {{ convertIso2Time(event.start.dateTime) }}
            img.local(src="@/assets/calendar-event-local.png")
            img.mr-1(src="@/assets/calendar-event-online.png")
            span.summary {{ event.summary }}
          //- resident in event
          .resident(v-else-if="event.hitomeQEvent.type == 4")
            span.start-time.mr-2 {{ convertIso2Time(event.start.dateTime) }}
            img.mr-1(src="@/assets/calendar-resident-in.png")
            span.summary {{ event.summary }}
          //- resident out event
          .resident(v-else-if="event.hitomeQEvent.type == 5")
            span.start-time.mr-2 {{ convertIso2Time(event.start.dateTime) }}
            img.mr-1(src="@/assets/calendar-resident-out.png")
            span.summary {{ event.summary }}
          //- visit reservation event
          .reservation(v-else-if="event.hitomeQEvent.type == 6")
            span.start-time.mr-2 {{ convertIso2Time(event.start.dateTime) }}
            img.mr-1(src="@/assets/calendar-reservation-visit.png")
            span.summary {{ event.summary }}
          //- online reservation event
          .reservation(v-else-if="event.hitomeQEvent.type == 7")
            span.start-time.mr-2 {{ convertIso2Time(event.start.dateTime) }}
            img.mr-1(src="@/assets/calendar-reservation-online.png")
            span.summary {{ event.summary }}
          //- inspection reservation event
          .inspection-reservation(v-else-if="event.hitomeQEvent.type == 8")
            span.start-time.mr-2 {{ convertIso2Time(event.start.dateTime) }}
            img.mr-1(src="@/assets/calendar-inspection-reservation.png")
            span.summary {{ event.summary }}
          //- online meeting event
          .reservation(v-else-if="event.hitomeQEvent.type == 9")
            span.start-time.mr-2 {{ convertIso2Time(event.start.dateTime) }}
            img.mr-1(src="@/assets/calendar-online-meeting.png")
            span.summary {{ event.summary }}
          //- original event
          .original(v-else-if="event.hitomeQEvent.type == 10")
            span.start-time.mr-2 {{ convertIso2Time(event.start.dateTime) }}
            span.summary {{ event.summary }}
          //- outing event
          .outing(v-else-if="event.hitomeQEvent.type == 11 || event.hitomeQEvent.type == 12")
            span.start-time.mr-2 {{ convertIso2Time(event.start.dateTime) }}
            span.summary {{ event.summary }}
          //- other event
          .other(v-else)
            span.start-time.mr-2 {{ convertIso2Time(event.start.dateTime) }}
            span.summary {{ event.summary }}
        //- blank because event already exists
        .event.blank.mb-1(v-else)

        //- event local event popover
        b-popover(v-if="event.hitomeQEvent.type == 1" :target="`${date.date}_${event.id}`" triggers="hover")
          .d-flex.flex-column
            .times
              b.start-time {{ convertIso2Time(event.start.dateTime) }} ～ {{ convertIso2Time(event.end.dateTime) }}
            .event-detail
              b.summary {{ event.summary }}
              br
              span 開催方法：現地
              br
              span 場所：{{ event.hitomeQEvent.source.place }}
        //- event online event popover
        b-popover(v-else-if="event.hitomeQEvent.type == 2" :target="`${date.date}_${event.id}`" triggers="hover")
          .d-flex.flex-column
            .times
              b.start-time {{ convertIso2Time(event.start.dateTime) }} ～ {{ convertIso2Time(event.end.dateTime) }}
            .event-detail
              b.summary {{ event.summary }}
              br
              span 開催方法：オンライン
              br
              span 場所：{{ event.hitomeQEvent.source.place }}
        //- event local and online event popover
        b-popover(v-else-if="event.hitomeQEvent.type == 3" :target="`${date.date}_${event.id}`" triggers="hover")
          .d-flex.flex-column
            .times
              b.start-time {{ convertIso2Time(event.start.dateTime) }} ～ {{ convertIso2Time(event.end.dateTime) }}
            .event-detail
              b.summary {{ event.summary }}
              br
              span 開催方法：現地・オンライン
              br
              span 場所：{{ event.hitomeQEvent.source.place }}
        //- resident in event popover
        b-popover(v-else-if="event.hitomeQEvent.type == 4" :target="`${date.date}_${event.id}`" triggers="hover")
          .d-flex.flex-column
            .times
              b.start-time {{ convertIso2Time(event.start.dateTime) }}
            .event-detail
              span 入所
              br
              span {{ event.hitomeQEvent.source.groupName }}
              br
              b {{ event.hitomeQEvent.source.room }}・{{ event.hitomeQEvent.source.name }}
        //- resident out event popover
        b-popover(v-else-if="event.hitomeQEvent.type == 5" :target="`${date.date}_${event.id}`" triggers="hover")
          .d-flex.flex-column
            .times
              b.start-time {{ convertIso2Time(event.start.dateTime) }}
            .event-detail
              span 退所
              br
              span {{ event.hitomeQEvent.source.groupName }}
              br
              b {{ event.hitomeQEvent.source.room }}・{{ event.hitomeQEvent.source.name }}
        //- visit reservation event popover
        b-popover(v-else-if="event.hitomeQEvent.type == 6" :target="`${date.date}_${event.id}`" triggers="hover")
          .d-flex.flex-column
            .times
              b.start-time {{ convertIso2Time(event.start.dateTime) }}
            .event-detail
              span 訪問面会
              br
              b {{ event.hitomeQEvent.source.residentRoom }}・{{ event.hitomeQEvent.source.residentName }}
              br
              span {{ event.hitomeQEvent.source.familyName }}・{{ event.hitomeQEvent.source.familyRelation }}
              .location(v-if="event.hitomeQEvent.source.location")
                span {{ event.hitomeQEvent.source.location }}
        //- online reservation event popover
        b-popover(v-else-if="event.hitomeQEvent.type == 7" :target="`${date.date}_${event.id}`" triggers="hover")
          .d-flex.flex-column
            .times
              b.start-time {{ convertIso2Time(event.start.dateTime) }}
            .event-detail
              span オンライン面会
              br
              b {{ event.hitomeQEvent.source.residentRoom }}・{{ event.hitomeQEvent.source.residentName }}
              br
              span {{ event.hitomeQEvent.source.familyName }}・{{ event.hitomeQEvent.source.familyRelation }}
        //- outing going out event popover
        b-popover(v-else-if="event.hitomeQEvent.type == 11" :target="`${date.date}_${event.id}`" triggers="hover")
          .d-flex.flex-column
            .times(v-if="isOverDay(event)")
              b.start-time {{ convertIso2Datetime(event.start.dateTime) }} ～ 
              b.end-time {{ convertIso2Datetime(event.end.dateTime) }}
            .times(v-else)
              b.start-time {{ convertIso2Time(event.start.dateTime) }} ～ 
              b.end-time {{ convertIso2Time(event.end.dateTime) }}
            .event-detail
              span 外出届け
              br
              b {{ event.hitomeQEvent.source.targetResidentInfo.roomName }}・{{ event.hitomeQEvent.source.targetResidentInfo.name }}
              br
              .family(v-if="event.hitomeQEvent.source.applicantPickup == true")
                span {{ event.hitomeQEvent.source.applicantFamilyInfo.name }}・{{ event.hitomeQEvent.source.applicantFamilyInfo.relation }}
              .family(v-else)
                span {{ event.hitomeQEvent.source.pickupPerson.name }}・{{ event.hitomeQEvent.source.pickupPerson.relation }}
        //- outing stying out event popover
        b-popover(v-else-if="event.hitomeQEvent.type == 12" :target="`${date.date}_${event.id}`" triggers="hover")
          .d-flex.flex-column
            .times(v-if="isOverDay(event)")
              b.start-time {{ convertIso2Datetime(event.start.dateTime) }} ～ 
              b.end-time {{ convertIso2Datetime(event.end.dateTime) }}
            .times(v-else)
              b.start-time {{ convertIso2Time(event.start.dateTime) }} ～ 
              b.end-time {{ convertIso2Time(event.end.dateTime) }}
            .event-detail
              span 外泊届け
              br
              b {{ event.hitomeQEvent.source.targetResidentInfo.roomName }}・{{ event.hitomeQEvent.source.targetResidentInfo.name }}
              br
              .family(v-if="event.hitomeQEvent.source.applicantPickup == true")
                span {{ event.hitomeQEvent.source.applicantFamilyInfo.name }}・{{ event.hitomeQEvent.source.applicantFamilyInfo.relation }}
              .family(v-else)
                span {{ event.hitomeQEvent.source.pickupPerson.name }}・{{ event.hitomeQEvent.source.pickupPerson.relation }}
        //- inspection reservation event popover
        b-popover(v-else-if="event.hitomeQEvent.type == 8" :target="`${date.date}_${event.id}`" triggers="hover")
          .d-flex.flex-column
            .times
              b.start-time {{ convertIso2Time(event.start.dateTime) }}
            .event-detail
              span 施設見学
              br
              b {{ event.hitomeQEvent.source.name }}
        //- online meeting event popover
        b-popover(v-else-if="event.hitomeQEvent.type == 9" :target="`${date.date}_${event.id}`" triggers="hover")
          .d-flex.flex-column
            .times
              b.start-time {{ convertIso2Time(event.start.dateTime) }}
            .event-detail
              span オンライン会議
              br
              b {{ event.hitomeQEvent.source.title }}
        //- original event popover
        b-popover(v-else-if="event.hitomeQEvent.type == 10" :target="`${date.date}_${event.id}`" triggers="hover")
          .d-flex.flex-column
            .times(v-if="isOverDay(event)")
              b.start-time {{ convertIso2Datetime(event.start.dateTime) }} ～ 
              b.end-time {{ convertIso2Datetime(event.end.dateTime) }}
            .times(v-else)
              b.start-time {{ convertIso2Time(event.start.dateTime) }} ～ 
              b.end-time {{ convertIso2Time(event.end.dateTime) }}
            .event-detail(style="white-space: pre-wrap;")
              .original-title
                b {{ event.summary }}
              .original-text {{ event.description }}
              .original-text 予定範囲：{{ getFilterLabel(event.hitomeQEvent.source) }}
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import moment, { Moment } from "moment";
import { nationalHolidaysStore } from "@/store/store";
import { NationalHolidaysGetModel } from "@/commons/services/NationalHolidays";
import { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import { CalendarEventGetModel, CalendarFilterType } from "@/commons/services/CalendarEvents";

/**
 * ### カレンダーコンポーネント(月表示)
 */
@Component({})
export default class CalendarOfMonth extends Vue {
  /** 現在日 */
  @Prop({ required: true })
  value!: Moment;
  /** イベント */
  @Prop({ required: true })
  events!: Array<CalendarEvent>;
  /** 月の変更ボタン */
  @Prop({ default: true })
  showChangeMonthBtn!: boolean;
  /** 1セル当たりの高さ */
  @Prop({ default: "70px" })
  cellHeight!: string;
  /** 小さく表示する */
  @Prop({ default: false })
  small!: boolean;

  /** 利用者グループMAP（key：id、value：グループ情報） */
  @Prop({ default: null })
  residentGroupMap!: Map<number, ResidentGroupsGetModel>;

  /** 祝日MAP */
  holidaysMap: Map<string, NationalHolidaysGetModel> = new Map();

  /** ヘッダー用 曜日 */
  readonly weekHeaders: Array<{ name: string; class: string }> = [
    { name: "日", class: "sunday" },
    { name: "月", class: "" },
    { name: "火", class: "" },
    { name: "水", class: "" },
    { name: "木", class: "" },
    { name: "金", class: "" },
    { name: "土", class: "saturday" }
  ];

  /** 現在年月日 */
  currentDate: Moment = moment();
  /** カレンダー開始日 */
  startDate: Moment | null = null;
  /** カレンダー開始日 */
  endDate: Moment | null = null;
  /** カレンダー行数 */
  row: number = 0;
  /** カレンダー配列(行数、日曜日～土曜日までの2次元配列) */
  calendar: Array<Array<CalendarDate>> = [];

  get sortedEvents() {
    const sortedEvents: Array<CalendarEvent> = this.events.slice().sort((a, b) => {
      let startDate = moment(a.start.date).format("YYYY-MM-DD");
      let startDate_2 = moment(b.start.date).format("YYYY-MM-DD");

      if (startDate < startDate_2) {
        return -1;
      }
      if (startDate > startDate_2) {
        return 1;
      }
      return 0;
    });

    return sortedEvents;
  }

  /**
   * ### カレンダーイベント更新検知
   *
   * - 新しいカレンダーイベントで画面情報を作り直す
   */
  @Watch("events")
  async changeEvents(newValue: Array<CalendarEvent>): Promise<void> {
    console.debug("changeEvents", newValue);
    await this.createView(this.value);
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.holidaysMap = this.createHolidaysMap(nationalHolidaysStore.holidays);
    await this.createView(this.value);
    return;
  }

  public createHolidaysMap(holidays: Array<NationalHolidaysGetModel>): Map<string, NationalHolidaysGetModel> {
    const holidaysMap: Map<string, NationalHolidaysGetModel> = new Map();
    for (const holiday of holidays) {
      holidaysMap.set(holiday.date, holiday);
    }
    return holidaysMap;
  }

  /**
   * ### 画面情報を作成する
   */
  public async createView(currentDate: Moment): Promise<void> {
    console.debug(`createView() statDate:${currentDate.format("YYYY-MM-DD")}`);

    const startDate: Moment = this.getStartDate(currentDate);
    this.startDate = startDate;
    console.debug(`createView() statDate:${startDate.format("YYYY-MM-DD")}`);

    const endDate: Moment = this.getEndDate(currentDate);
    this.endDate = endDate;
    console.debug(`createView() endDate:${endDate.format("YYYY-MM-DD")}`);

    const rowLength: number = Math.ceil(endDate.diff(startDate, "days") / 7); // 月終わりと月初めの日にち差分 / 7で表示行数
    this.row = rowLength;

    const calendar: Array<Array<CalendarDate>> = this.createCalendar(startDate, rowLength, this.value, this.sortedEvents); // 日付ソートしたevent(sortedEvents)を渡す
    this.calendar = calendar;

    return;
  }

  /**
   * ### カレンダーの開始日を設定したMomentオブジェクトを取得する
   *
   * - 現在日をコピーし、月初めに日付を設定する
   * - 月初めの曜日を取得する
   * - カレンダーの開始日は月初めの週の日曜日になるのでMomentオブジェクトを設定する
   *   - カレンダーの開始日は前月の日付もありえる(例えば月初めが火曜日の場合、2日前が前月の30日になる等)
   *   - 月初め(1日) - 月初めの曜日から日曜日までの差分(0～6日) = カレンダーの開始日
   */
  public getStartDate(currentDate: Moment): Moment {
    const tempDate: Moment = currentDate.clone();
    tempDate.startOf("month");
    const weekNum: number = tempDate.day();
    const startDate: Moment = tempDate.subtract(weekNum, "days");

    return startDate;
  }

  /**
   * ### カレンダーの最終日を設定したMomentオブジェクトを取得する
   *
   * - 現在日をコピーし、月終わりに日付を設定する
   * - 月終わりの曜日を取得する
   * - カレンダーの最終日は月終わりの週の土曜日になるのでMomentオブジェクトを設定する
   *   - カレンダーの最終日は次月の日付もありえる(例えば月終わりが木曜日の場合、2日後が次月の2日になる等)
   *   - 月終わり(n日) + 土曜日から月終わりの曜日の差分(0～6日) = カレンダーの最終日
   */
  public getEndDate(currentDate: Moment): Moment {
    const tempDate: Moment = currentDate.clone();
    tempDate.endOf("month");
    const weekNum: number = tempDate.day();
    const endDate: Moment = tempDate.add(6 - weekNum, "days");

    return endDate;
  }

  /**
   * ### カレンダーの作成
   *
   * - 以下ループでカレンダー用の行・列の二次元配列を作成する
   *   - 表示対象の行数(日曜日始まりで考慮した必要行数)分ループして行情報を作成する
   *   - 日曜日～土曜日の1週間=7日分ループ
   * - 日曜日～土曜日までループ
   *   - Momentから日付を取得してカレンダー用配列に追加
   *   - Momentから月を取得して今月か否かを判定してカレンダー用配列に追加
   *   - カレンダーイベントを作成してカレンダー用配列に追加
   *   - Momentオブジェクトの日付を1日進める
   *
   * @param startDate 開始日
   * @param rowLength 表示対象の行数
   * @param currentDate 現在日
   * @param events 全カレンダーイベント
   */
  public createCalendar(startDate: Moment, rowLength: number, currentDate: Moment, events: Array<CalendarEvent>): Array<Array<CalendarDate>> {
    let rows: Array<Array<CalendarDate>> = [];
    let date: Moment = startDate.clone();
    const currentMonth: number = currentDate.get("month");
    let foundedPreviousMonth: boolean = false;
    let foundedNextMonth: boolean = false;

    console.debug("createCalendar", events);

    for (let week = 0; week < rowLength; week++) {
      let columns: Array<CalendarDate> = [];

      for (let day = 0; day < 7; day++) {
        const dayEvents: Array<CalendarEvent> = this.createEvents(date, events, day);
        const isCurrentMonth: boolean = currentMonth === date.get("month") ? true : false;
        const isCurrentDate: boolean = isCurrentMonth == true && this.currentDate.format("YYYY-MM-DD") === date.format("YYYY-MM-DD") ? true : false;
        let showMonth: boolean = false;
        if (date.get("month") < currentMonth && foundedPreviousMonth == false) {
          showMonth = true;
          foundedPreviousMonth = true;
        }
        if (currentMonth < date.get("month") && foundedNextMonth == false) {
          showMonth = true;
          foundedNextMonth = true;
        }

        columns.push({
          date: date.get("date"),
          month: date.get("month"),
          year: date.get("year"),
          week: this.convertWeekName(date),
          isCurrentMonth,
          isCurrentDate,
          showMonth,
          events: dayEvents
        });
        date.add(1, "days");
      }

      rows.push(columns);
    }

    return rows;
  }

  public convertWeekName(date: Moment): string {
    switch (date.format("d")) {
      case "0": {
        return "sunday";
      }
      case "1": {
        return "monday";
      }
      case "2": {
        return "tuesday";
      }
      case "3": {
        return "wednesday";
      }
      case "4": {
        return "thursday";
      }
      case "5": {
        return "friday";
      }
      case "6": {
        return "saturday";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### 指定日付のカレンダーイベントを作成する
   *
   * @param date 指定日付
   * @param events 月全体のカレンダーイベント(日付ソート済み前提)
   * @param day 曜日番号（日曜：0～土曜：6）
   */
  public createEvents(targetDate: Moment, events: Array<CalendarEvent>, day: number): Array<CalendarEvent> {
    let targetEvents: Array<CalendarEvent> = [];

    // 分岐の「開始日が指定日付」「曜日が日曜日(0)」以外のイベントは空の帯として表示するイベントなので保持する
    let startedEvents: Array<CalendarEvent> = [];
    // stackIndexをチェックすることで指定日付で開始されているイベントが、すでにどの場所に表示されているのか識別する
    let stackIndex: number = 0;

    events.forEach((event) => {
      // イベント開始日、イベント終了日、指定日をYYYY-MM-DD形式で持つ
      const startDate: string = moment(event.start.date).format("YYYY-MM-DD");
      const endDate: string = moment(event.end.date).format("YYYY-MM-DD");
      const date: string = targetDate.format("YYYY-MM-DD");
      // 作成するイベントの開始日
      let eventDate: string | null = null;

      if (startDate <= date && endDate >= date) {
        // 開始日が指定日付
        if (startDate === date) {
          eventDate = event.start.date as string;
        }
        // 曜日が日曜日(0)
        else if (day === 0) {
          eventDate = date;
        }

        // 作成するイベントの開始日が存在する場合は表示用のイベントを作成する。無ければ既にイベントは作成済みなので空の帯として表示するイベントを追加する
        if (eventDate != null) {
          const targetDateStartedEvents: Array<CalendarEvent> = this.getTargetDateStartedEvents(stackIndex, startedEvents);
          targetEvents = targetEvents.concat(targetDateStartedEvents);
          stackIndex += targetDateStartedEvents.length;
          // stackIndexはダミー作成で使用するのでevent自体に格納する
          event.hitomeQEvent.stackIndex = stackIndex;

          const width = this.calculateEventWidth(eventDate, event.end.date as string, day);
          targetEvents.push(this.createTargetEvent(event, width));
          stackIndex++;
        } else {
          startedEvents.push(event);
        }
      }
    });

    return targetEvents;
  }

  /**
   * ### 既に開始されているカレンダーイベントを取得する
   *
   * - 新規にカレンダーイベントを追加する場合、カレンダーイベントの帯が重なってはいけない
   * - 重ならないようにするため、「既に開始されているイベント」を取得する
   * - 引数stackIndexを使用して「既に開始されているイベント(startedEvents)」を検索し、見つからなくなるまで取得して返却する
   */
  public getTargetDateStartedEvents(stackIndex: number, startedEvents: Array<CalendarEvent>): Array<CalendarEvent> {
    let targetDateStartedEvents: Array<CalendarEvent> = [];
    let tempStackIndex: number = stackIndex;
    let startedEvent: CalendarEvent | undefined;

    do {
      startedEvent = startedEvents.find((event) => event.hitomeQEvent.stackIndex == tempStackIndex);

      if (startedEvent) {
        targetDateStartedEvents.push(startedEvent);
        tempStackIndex++;
      }
    } while (typeof startedEvent !== "undefined");

    return targetDateStartedEvents;
  }

  /**
   * ### カレンダーイベントの横幅を計算する
   *
   * - 開始日と終了日の日数差分を計算して横幅の計算元にする
   * - (週末(土曜) - 現在曜日) < 日数差分 の場合、カレンダーからはみ出すので週末までの横幅を計算する
   * - その他の場合、日数差分を使用してそのまま横幅を計算する
   */
  public calculateEventWidth(startDate: string, endDate: string, day: number): string {
    const betweenDays: number = moment(endDate).diff(moment(startDate), "days");

    if (6 - day < betweenDays) {
      // 週跨ぎのパターン。横幅は少し短く表示する1日分(95%) + 塗りつぶしの分(日数*100%) + 表の枠線(日数*1px)
      return `calc(${(6 - day) * 100 + 95}% + ${(6 - day) * 1}px)`;
    } else {
      // 週跨ぎしないパターン。横幅は少し短く表示する1日分(95%) + 塗りつぶしの分(日数*100%) + 表の枠線(日数*1px)
      return `calc(${betweenDays * 100 + 95}% + ${betweenDays * 1}px)`;
    }
  }

  /**
   * ### 指定日のカレンダーイベントを作成する
   */
  public createTargetEvent(event: CalendarEvent, width: string): CalendarEvent {
    // カレンダーイベント用のデータを格納する。eventは参照渡し、widthは実体渡しをすることでstartedEventsからダミー情報を作る際にwidthは参照渡しされない
    return {
      ...event,
      hitomeQEvent: {
        ...event.hitomeQEvent,
        width: width
      }
    };
  }

  /**
   * ### ISO年月日時を画面表示用の時間に変換する
   */
  public convertIso2Time(isoDateTime: string): string {
    const datetime: Moment = moment(isoDateTime);

    return datetime.format("HH:mm");
  }

  /**
   * ### ISO年月日時を画面表示用の日時に変換する
   */
  public convertIso2Datetime(isoDateTime: string): string {
    const datetime: Moment = moment(isoDateTime);

    return datetime.format("MM/DD HH:mm");
  }

  /**
   * ### 日跨ぎのイベントかを返す
   */
  public isOverDay(event: CalendarEvent): boolean {
    const start: Moment = moment(event.start.dateTime);
    const end: Moment = moment(event.end.dateTime);

    return end.isAfter(start, "day");
  }

  /**
   * ### 前の月ボタン押下イベント
   *
   * - 現在年月を再表示したいので、現在日をコピーして月を減算し、現在日に格納しなおす
   * - 画面を再描画する
   */
  public onPreviousMonth(): void {
    let newCurrentDate: Moment = this.value.clone();
    newCurrentDate.subtract(1, "month");
    this.$emit("input", newCurrentDate);

    this.createView(newCurrentDate);

    return;
  }

  /**
   * ### 今月ボタン押下イベント
   *
   * - 現在年月を再表示したいので、当日を作成し、現在日に格納しなおす
   * - 画面を再描画する
   */
  public onCurrentMonth(): void {
    let newCurrentDate: Moment = moment();
    this.$emit("input", newCurrentDate);

    this.createView(newCurrentDate);

    return;
  }

  /**
   * ### 次の月ボタン押下イベント
   *
   * - 現在年月を再表示したいので、現在日をコピーして月を加算し、現在日に格納しなおす
   * - 画面を再描画する
   */
  public onNextMonth(): void {
    let newCurrentDate: Moment = this.value.clone();
    newCurrentDate.add(1, "month");
    this.$emit("input", newCurrentDate);

    this.createView(newCurrentDate);

    return;
  }

  /**
   * ### イベントのstyle属性を作成する
   */
  public createEventStyle(event: CalendarEvent): string {
    const styles: string[] = [];
    styles.push(`width: ${event.hitomeQEvent.width};`);
    styles.push(`color: ${event.hitomeQEvent.color};`);
    styles.push(`background-color: ${event.hitomeQEvent.bgColor};`);
    if (event.hitomeQEvent.borderColor) {
      styles.push(`border: solid 2px ${event.hitomeQEvent.borderColor};`);
    } else {
      styles.push(`border: solid 2px ${event.hitomeQEvent.bgColor};`);
    }
    return styles.join(" ");
  }

  /**
   * ### イベント押下
   */
  public onEvent(event: CalendarEvent): void {
    this.$emit("onEvent", event);
    return;
  }

  /**
   * ### 曜日のCSSクラスを取得する
   */
  public getWeekHeaderClass(weekClass: { name: string; class: string }, small: boolean): string[] {
    let classNames: string[] = [];

    classNames.push(weekClass.class);
    if (small == true) {
      classNames.push("date-small");
    }

    return classNames;
  }

  /**
   * ### 日付のCSSクラスを取得する
   */
  public getDateClass(date: CalendarDate): string[] {
    const classNames: string[] = [];

    classNames.push(date.week);
    if (this.getHolidayName(date)) {
      classNames.push("holiday");
    }

    return classNames;
  }

  /**
   * ### 祝日名を取得する
   */
  public getHolidayName(date: CalendarDate): string {
    const holiday: Date = new Date(`${date.year}-${date.month + 1}-${date.date}`);
    const holidayName: NationalHolidaysGetModel | undefined = this.holidaysMap.get(this.$dateUtil.convertDateToString(holiday, "yyyy-mm-dd"));
    if (holidayName) {
      return holidayName.name;
    } else {
      return "";
    }
  }

  getFilterLabel(event: CalendarEventGetModel): string {
    if (event.filterType == CalendarFilterType.FACILITY) {
      return "施設全体";
    } else if (event.filterType == CalendarFilterType.RESIDENT_GROUP) {
      const names: string[] = [];
      for (const id of event.residentGroups) {
        const group: ResidentGroupsGetModel | undefined = this.residentGroupMap.get(id);
        if (group) {
          names.push(group.name);
        }
      }
      return names.join("、");
    }
    return "";
  }
}

/**
 * ### カレンダー日付
 */
export interface CalendarDate {
  /** カレンダー表示用の日付 */
  date: number;
  /** カレンダー表示用の月 */
  month: number;
  /** 年 */
  year: number;
  /** 曜日 */
  week: string;
  /** 今月の日付か否か(今月ならtrue) */
  isCurrentMonth: boolean;
  /** 今日の日付か否か(今日ならtrue) */
  isCurrentDate: boolean;
  /** 月も表示させるか否か */
  showMonth: boolean;
  /** 日付にひもづくカレンダーイベント */
  events: Array<CalendarEvent>;
}

/**
 * ### カレンダーイベント
 *
 * - Googleのカレンダーイベントと親和性のあるプロパティ構成にしている
 * - types
 *   - https://github.com/googleapis/google-api-nodejs-client/blob/623c06c1bb66cb0b7b6c57713b589cdd3b492169/src/apis/calendar/v3.ts
 * - resource
 *   - https://developers.google.com/calendar/api/v3/reference/events
 * - 本アプリ独自プロパティを追加している
 */
export interface CalendarEvent {
  /** ID */
  id: number;
  /** イベントのタイトル */
  summary: string;
  /** イベントの説明。HTMLを含めることができます。オプション。 */
  description?: string;
  /** イベントの（包括的）開始時間。定期的なイベントの場合、これは最初のインスタンスの開始時刻です。 */
  start: CalendarEventDateTime;
  /** イベントの（排他的な）終了時間。定期的なイベントの場合、これは最初のインスタンスの終了時刻です。 */
  end: CalendarEventDateTime;
  /** 本アプリ独自プロパティ */
  hitomeQEvent: CalendarHitomeQEvent;
}

/**
 * ### カレンダーイベント日時
 */
export interface CalendarEventDateTime {
  /**
   * The date, in the format "yyyy-mm-dd", if this is an all-day event.
   * これが終日のイベントである場合は、「yyyy-mm-dd」の形式の日付。
   */
  date?: string | null;
  /**
   * The time, as a combined date-time value (formatted according to RFC3339).
   * A time zone offset is required unless a time zone is explicitly specified in timeZone.
   * 結合された日時値としての時刻（RFC3339に従ってフォーマットされます）。タイムゾーンがで明示的に指定されていない限り、タイムゾーンオフセットが必要ですtimeZone。
   */
  dateTime?: string | null;
  /**
   * The time zone in which the time is specified. (Formatted as an IANA Time Zone Database name, e.g. "Europe/Zurich".)
   * For recurring events this field is required and specifies the time zone in which the recurrence is expanded.
   * For single events this field is optional and indicates a custom time zone for the event start/end.
   * 時刻が指定されているタイムゾーン。（IANAタイムゾーンデータベース名としてフォーマットされます（例：「Europe / Zurich」）。）
   * 定期的なイベントの場合、このフィールドは必須であり、定期的なイベントが展開されるタイムゾーンを指定します。
   * 単一イベントの場合、このフィールドはオプションであり、イベントの開始/終了のカスタムタイムゾーンを示します。
   */
  timeZone?: string | null;
}

/**
 * ### カレンダーHitomeQイベント
 *
 * - カレンダーイベント自体はGoogleカレンダーイベントの形式に合わせるため、アプリ独自情報を持たせる
 */
export interface CalendarHitomeQEvent {
  /** イベント文字色 */
  color: string;
  /** イベント背景色 */
  bgColor: string;
  /** ボーダー色 */
  borderColor?: string;
  /** 横幅 */
  width?: string;
  /** CalendarDateに格納する際、管理用に1日内で一意のIDを割り振る */
  stackIndex?: number;
  /** 種別 */
  type: CalendarHitomeQEventType;
  /**
   * データソース
   * 編集画面へ遷移する、イベントを削除する、詳細をモーダル表示する等でキーや表示項目が必要な場合のためにデータを保持させる
   **/
  source: any;
}

export enum CalendarHitomeQEventType {
  /** 初期値 */
  UNKNOWN = -1,
  /** イベント現地開催のみ */
  EVENT_LOCAL_ONLY = 1,
  /** イベントオンラインのみ */
  EVENT_ONLINE_ONLY = 2,
  /** イベント現地・オンライン開催 */
  EVENT_LOCAL_AND_ONLINE = 3,
  /** 利用者入所日 */
  RESIDENT_IN_DATE = 4,
  /** 利用者退所日 */
  RESIDENT_OUT_DATE = 5,
  /** 面会予約（訪問） */
  RESERVATION_VISIT = 6,
  /** 面会予約（オンライン） */
  RESERVATION_ONLINE = 7,
  /** 施設見学予約 */
  INSPECTION_RESERVATION = 8,
  /** オンライン会議 */
  ONLINE_MEETING = 9,
  /** オリジナルイベント */
  MEMO = 10,
  /** 外出届け */
  OUTING_GOING_OUT = 11,
  /** 外泊届け */
  OUTING_STAYING_OUT = 12
}
</script>

<style lang="scss" scoped>
@import "../../styles/pc/colors.scss";

.calendar-of-month {
  .change-month-btn {
    font-size: 14px !important;
    min-width: 90px !important;
    height: 30px !important;
  }

  // 曜日名のリスト
  .week-header {
    border-top: 1px solid $border-darkgray;

    .date {
      background-color: $bg-header;
      font-weight: bold;
      text-align: center;
      padding: 0.5rem;
    }
    .date.date-small {
      padding: 0.2rem;
    }
  }
  // 曜日名、カレンダー共通
  .week,
  .week-header {
    border-left: 1px solid $border-darkgray;

    .date {
      width: calc(100% / 7); // 親要素を曜日で分割した横幅を取る
      border-right: 1px solid $border-lightgray;
    }
    .date:last-child {
      border-right: 1px solid $border-darkgray;
    }
  }
  // カレンダーのリスト
  .week {
    border-top: 1px solid $border-lightgray;

    .date {
      .date-num {
        margin: 0.5rem 0 0.5rem 0.5rem;
      }
      .date-num.date-num-small {
        margin: 0;
      }
    }
    .is-non-current-month {
      background-color: $bg-calendar-non-current-month;
    }
    .is-current-date {
      background-color: $bg-calendar-current-date;
    }
    .event {
      margin-left: 1px;
      padding: 2px 8px;
      height: 24px;
      font-size: 12px;

      // positionとz-indexの組み合わせでイベントの帯がカレンダーの罫線の上にかかるようにする
      position: relative;
      z-index: 100;
      border-radius: 4px;

      .facility-event,
      .resident,
      .reservation,
      .outing,
      .inspection-reservation,
      .original,
      .other {
        // 最大横幅を超えたら省略記号で表示する
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
      .facility-event > img,
      .reservation > img {
        margin-top: -3px;
      }
      .facility-event > img.local {
        margin-top: -5px;
      }
      .resident > img {
        height: 18px;
        margin-top: -1px;
      }
      .inspection-reservation > img {
        margin-top: -3px;
      }

      .original {
        cursor: pointer;
      }
    }

    .start-time {
      font-weight: bold;
    }
  }
  .week:last-child {
    border-bottom: 1px solid $border-darkgray;
  }
}
</style>
